// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../services/api';
import { EmailVerificationModal } from '../components/auth/EmailVerificationModal';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const { login } = useAuthStore();
  const [isLogin, setIsLogin] = useState(true);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (invite) {
      setInvitationToken(invite);
      setIsLogin(false);
    } else if (params.get('register') === 'true') {
      setIsLogin(false);
    }
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!isLogin && password !== confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        const response = await authAPI.login(email, password);
        const { access_token, refresh_token, user } = response.data;
        login(user, access_token, refresh_token);
      } else {
        const res = await authAPI.register({ email, username, password, invitation_token: invitationToken || undefined });
        setPassword('');
        setConfirmPassword('');
        if (res.data?.requires_verification === false) {
          // Account was claimed / already verified — go straight to sign-in.
          setIsLogin(true);
          setSuccess(t('auth.accountReady'));
        } else {
          setRegisteredEmail(email);
          setShowEmailVerification(true);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('auth.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { title: t('nav.orderBoard'), description: t('orderBoard.subtitle') },
    { title: t('nav.prepQueue'), description: t('prep.subtitle') },
    { title: t('nav.routeBuilder'), description: t('nav.dispatch') },
    { title: t('nav.driverView'), description: t('nav.delivery') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-900 via-rose-700 to-pink-900">
      <header className="px-8 py-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500">
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2c1.5 2 4 2.5 4 5a4 4 0 11-8 0c0-2.5 2.5-3 4-5z" opacity=".9" />
                <path d="M12 11c0 4 3 5 6 5-1 3-4 4-6 4s-5-1-6-4c3 0 6-1 6-5z" />
              </svg>
            </div>
            <span className="text-2xl font-bold text-white">{t('app.name')}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-8 py-12">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="space-y-6">
            <h1 className="text-4xl font-bold leading-tight text-white md:text-5xl">{t('app.tagline')}</h1>
            <p className="text-xl text-rose-100">
              {t('orderBoard.subtitle')} — {t('prep.subtitle')}.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-300">−50%</div>
                <div className="text-sm text-rose-200">Missed deliveries</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-300">&lt;5 min</div>
                <div className="text-sm text-rose-200">Route planning</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-300">200+</div>
                <div className="text-sm text-rose-200">Orders / day</div>
              </div>
            </div>
          </div>

          <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-6 flex border-b border-gray-200">
              <button
                className={`flex-1 pb-3 text-center font-medium transition ${
                  isLogin ? 'border-b-2 border-rose-600 text-rose-600' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => { setIsLogin(true); setError(''); setSuccess(''); }}
              >
                {t('auth.signIn')}
              </button>
              <button
                className={`flex-1 pb-3 text-center font-medium transition ${
                  !isLogin ? 'border-b-2 border-rose-600 text-rose-600' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => { setIsLogin(false); setError(''); setSuccess(''); }}
              >
                {t('auth.register')}
              </button>
            </div>

            {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            {success && <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{success}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('auth.username')}</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-rose-500"
                    required={!isLogin}
                  />
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('auth.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-rose-500"
                  placeholder="you@shop.com"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('auth.password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-rose-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              {!isLogin && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('auth.confirmPassword')}</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-rose-500"
                      required={!isLogin}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-rose-600 px-4 py-3 font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? t('auth.processing') : isLogin ? t('auth.signIn') : t('auth.createAccount')}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              {isLogin ? `${t('auth.noAccount')} ` : `${t('auth.haveAccount')} `}
              <button
                onClick={() => { setIsLogin(!isLogin); setError(''); setSuccess(''); }}
                className="font-medium text-rose-600 hover:text-rose-700"
              >
                {isLogin ? t('auth.register') : t('auth.signIn')}
              </button>
            </p>
          </div>
        </div>

        <section className="mt-24">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {features.map((f, i) => (
              <div key={i} className="rounded-xl bg-white/10 p-6 backdrop-blur-sm transition hover:bg-white/20">
                <h3 className="mb-2 text-lg font-semibold text-white">{f.title}</h3>
                <p className="text-sm text-rose-100">{f.description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="mt-24 border-t border-rose-800 py-8">
        <div className="mx-auto max-w-7xl px-8 text-center">
          <p className="text-sm text-rose-200">{t('app.name')} — {t('app.tagline')}</p>
          <p className="mt-2 text-xs text-rose-300">Part of the Auxilio Suite</p>
        </div>
      </footer>

      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        onSuccess={() => {
          setShowEmailVerification(false);
          setIsLogin(true);
          setSuccess(t('auth.verifySuccess'));
        }}
        email={registeredEmail}
      />
    </div>
  );
};

export default LandingPage;
